<template>
  <div id="app" :touch="hasTouch">
    <header>
      <div class="menu-btn" v-if="view === 'map'">
        <button :class="`ic ic-${menuOpened ? 'close' : 'menu'}`" @click="toggleMenu"></button>
      </div>
      <div class="logo">
        <img src="@/assets/images/tdf_logo.jpg" alt="Tour de France - Ireland Edition">
      </div>
      <button class="ic ic-info" @click="dialog = 'info'"></button>
    </header>
    <div class="controls-bar">
      <div class="view-settings">
        <i class="el-icon-map-location" :active="view === 'map'"></i>
        <el-switch v-model="view" active-value="card" inactive-value="map" active-color="#dcdfe6" inactive-color="#dcdfe6" />
        <i class="el-icon-menu" :active="view === 'card'"></i>
      </div>
      <div class="controls-filters">
        <el-button icon="el-icon-set-up" size="small" :type="filtersOpened ? 'primary' : null" @click="filtersOpened = !filtersOpened">Filters</el-button>
        <div class="filters" :opened="filtersOpened">
          <el-checkbox v-model="filters.covid19">Open during COVID-19</el-checkbox>
          <el-select
            v-model="filters.types"
            multiple
            size="small"
            collapse-tags
            placeholder="Types">
            <el-option
              v-for="type in types"
              :key="type"
              :label="type"
              :value="type">
            </el-option>
          </el-select>
          <el-select
            v-model="filters.locations"
            multiple
            size="small"
            collapse-tags
            placeholder="Locations">
            <el-option-group label="Regions">
              <el-option
                v-for="region in regions"
                :key="region"
                :label="region"
                :value="region">
              </el-option>
            </el-option-group>
            <el-option-group label="Counties">
              <el-option
                v-for="county in counties"
                :key="county"
                :label="county"
                :value="county">
              </el-option>
            </el-option-group>
          </el-select>
          <el-select
            v-model="sortMethod"
            size="small"
            placeholder="Sort">
            <el-option label="Newest first" value="newest"></el-option>
            <el-option label="By Name" value="name"></el-option>
            <el-option label="By Type" value="type"></el-option>
          </el-select>
        </div>
      </div>
      <el-input
        size="small"
        placeholder="Search"
        suffix-icon="el-icon-search"
        v-model="filters.terms">
      </el-input>
    </div>
    <CardView v-if="view === 'card'" :companies="companies" />
    <MapView v-else :companies="companies" />
    <footer>
      <div class="footer-left">
        <a href="https://ie.ambafrance.org/" target="_blank">
          <div class="embassy-info">
            <b>France in Ireland</b> - Embassy of France - Consular Agency in Galway
          </div>
        </a>
      </div>
      <div class="footer-right">
        <div class="credits">
          <a href="https://blackpugstudio.com" target="_blank" rel="noopener noreferrer">Web design</a> made with ❤️ by Black Pug Studio
          <!-- <div>Technology: Firebase | VueJS | Google Maps</div> -->
        </div>
      </div>
    </footer>
    <div class="overlay" :opened="Boolean(dialog)" @click="dialog = null">
      <div v-if="dialog === 'info'" class="dialog" @click.stop>
        <a class="ic ic-close" @click="dialog = null"></a>
        <div class="dialog-title">About Tour de France Eire</div>
        <div class="dialog-content">
          <div class="infos">
            <div class="info-section">
              <h4>Information</h4>
              <p>Tour de France – Ireland Edition is to give you a simple overview of some French businesses established in Ireland and access details available.<br><br>This site is an initiative from Catherine Gagneux, French Honorary Consul Galway-Connacht.<br><br>As a French entrepreneur in Ireland, you can also add your own details. This is a free registration.</p>
            </div>
            <div class="info-section">
              <h4>Contact us</h4>
              <p>Queries may be submitted to <a href="mailto:tourdefrance.eire@gmail.com">tourdefrance.eire@gmail.com</a><br>This page has been designed by <a href="https://www.blackpugstudio.com" target="_blank">Black Pug Studio</a></p>
            </div>
            <div class="info-section">
              <h4>Disclaimers</h4>
              <p>Limit of Liability: Tour de France Ireland Edition does not guarantee results or accuracy from the use of listed names (those details are managed by each of the businesses), nor shall they be liable for any loss or damages sustained by the user through the use of any list.<br><br>List details are approximate and subject to change due to constant updates and revisions.</p>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <div><a href="https://blackpugstudio.com" target="_blank" rel="noopener noreferrer">Web design</a> made with ❤️ by Black Pug Studio using Firebase, VueJS and Google Maps</div>
          <a href="https://ie.ambafrance.org/" target="_blank" rel="noopener noreferrer">
            <img src="@/assets/images/rep-fr-logo.svg" alt="Embassy of France">
          </a>
          <a href="https://www.blackpugstudio.com" target="_blank">
            <img src="@/assets/images/bps_logo-b.svg" alt="Black Pug Studio Ltd">
          </a>
          <a href="https://www.businessfrance.fr/en/home" target="_blank">
            <img src="@/assets/images/tdf_logo_businessfrance.png" alt="Business France">
          </a>
        </div>
      </div>
      <div v-else-if="dialog === 'company'" class="dialog" v-loading="saving" @click.stop>
        <a class="ic ic-close" @click="dialog = null"></a>
        <div class="dialog-title">Add your business</div>
        <div class="dialog-content">
          <el-form
            ref="companyForm"
            size="small"
            :model="companyForm"
            :rules="rules"
            label-position="top"
          >
            <el-row>
              <el-col :span="12">
                <el-form-item label="Name" prop="name">
                  <el-input name="name" v-model="companyForm.name"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Type" prop="type">
                  <el-select name="type" v-model="companyForm.type">
                    <el-option v-for="type in types" :label="type" :value="type" :key="`type_${type}`"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Email" prop="email">
                  <el-input name="email" v-model="companyForm.email"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Phone" prop="phone">
                  <el-input class="tel-input" name="phone" v-model="companyForm.phone" type="tel"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="Address" prop="address">
                  <el-input name="address" v-model="companyForm.address"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="City" prop="city">
                  <el-input name="city" v-model="companyForm.city"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="County" prop="county">
                  <el-select name="county" v-model="companyForm.county">
                    <el-option :label="county" :value="county" v-for="county in counties" :key="county"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Region" prop="region">
                  <el-select name="region" v-model="companyForm.region">
                    <el-option :label="region" :value="region" v-for="region in regions" :key="region"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Website" prop="website">
                  <el-input name="website" v-model="companyForm.website"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Facebook" prop="facebook">
                  <el-input name="facebook" v-model="companyForm.facebook"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Twitter" prop="twitter">
                  <el-input name="twitter" v-model="companyForm.twitter"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="LinkedIn" prop="linkedin">
                  <el-input name="linkedin" v-model="companyForm.linkedin"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Instagram" prop="instagram">
                  <el-input name="instagram" v-model="companyForm.instagram"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="Description" prop="description">
                  <el-input
                    type="textarea"
                    name="description"
                    v-model="companyForm.description"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-checkbox name="covid19" v-model="companyForm.covid19">Open during COVID-19</el-checkbox>
              </el-col>
            </el-row>
            <div class="form-actions">
              <div>
                <el-button @click="dialog = null" :disabled="saving">Cancel</el-button>
                <el-button type="primary" :loading="saving" @click="submitCompany">Apply</el-button>
              </div>
            </div>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MapView from './components/MapView.vue'
import CardView from './components/CardView.vue'
import { Notification } from 'element-ui'

export default {
  name: 'app',
  data() {
    return {
      view: 'card',
      allCompanies: [],
      sortMethod: 'newest',
      filtersOpened: false,
      hasTouch: 'ontouchstart' in window,
      rules: {
        name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 80, message: "Limited to 80 characters", trigger: "blur" }
        ],
        type: [
          { required: true, message: 'Required', trigger: 'blur' }
        ],
        address: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 300, message: "Limited to 300 characters", trigger: "blur" }
        ],
        city: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 80, message: "Limited to 80 characters", trigger: "blur" }
        ],
        county: [
          { required: true, message: 'Required', trigger: 'blur' }
        ],
        region: [
          { required: true, message: 'Required', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 20, message: "Limited to 20 characters", trigger: "blur" },
          {
            trigger: ['blur', 'change'],
            validator: (rule, value, cb) => {
              if(/^[\d+]+$/.test(value)) cb()
              else cb(new Error("You can only use numbers and the character '+' in this field."))
            }
          }
        ],
        description: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 1000, message: "Limited to 1000 characters", trigger: "blur" }
        ],
        website: [
          { max: 80, message: "Limited to 80 characters", trigger: "blur" }
        ],
        facebook: [
          { max: 300, message: "Limited to 300 characters", trigger: "blur" }
        ],
        twitter: [
          { max: 300, message: "Limited to 300 characters", trigger: "blur" }
        ],
        linkedin: [
          { max: 300, message: "Limited to 300 characters", trigger: "blur" }
        ],
        instagram: [
          { max: 300, message: "Limited to 300 characters", trigger: "blur" }
        ],
        email: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 255, message: "Limited to 255 characters", trigger: "blur" },
          { type: 'email', message: 'Please input correct email address', trigger: ['blur', 'change'] }
        ],
      },
      dialog: null,
      saving: false,
      menuOpened: false,
      companyForm: {
        type: '',
        name: '',
        address: '',
        city: '',
        county: '',
        email: '',
        description: '',
        covid19: false,
        facebook: '',
        twitter: '',
        linkedin: '',
        instagram: '',
        website: '',
        phone: '',
      },
      filters: {
        terms: '',
        types: [],
        locations: [],
        covid19: false
      },
      types: [
        "Bar",
        "Cafe",
        "Industry",
        "Manufacturing",
        "Farming",
        "Craft",
        "Restaurant",
        "Technology",
        "Shop",
        "Hotel",
        "Hairdresser",
        "Wholesale",
        "Health",
        "Social/Health Service",
        "Government Body",
        "Photographer",
        "Media",
        "Artist",
        "Education",
        "Bakery",
        "Association",
        "Leisure",
        "Beauty Salon",
        "Supplier",
        "Retailer",
        "Consultant",
        "Bank/Finance Services",
        "Transportation",
        "Medical Industry",
        "Seafood/Fishing",
        "Other"
      ],
      counties: [
        'Carlow',
        'Cavan',
        'Clare',
        'Cork',
        'Donegal',
        'Dublin',
        'Galway',
        'Kerry',
        'Kildare',
        'Kilkenny',
        'Laois',
        'Leitrim',
        'Limerick',
        'Longford',
        'Louth',
        'Mayo',
        'Meath',
        'Monaghan',
        'Offaly',
        'Roscommon',
        'Sligo',
        'Tipperary',
        'Waterford',
        'Westmeath',
        'Wexford',
        'Wicklow'
      ],
      regions: [
        'Connaught',
        'Leinster',
        'Munster',
        'Ulster'
      ],
    }
  },
  watch: {
    view(view) {
      if(view !== 'map') {
        this.menuOpened = false
      }
    }
  },
  computed: {
    companies() {
      const { types, locations, covid19, terms } = this.filters
      return this.allCompanies.filter(company => {
        if(covid19 && !company.covid19) return false
        if(terms && !company.name.toLowerCase().includes(terms.toLowerCase())) return false
        if(types.length && !types.includes(company.type)) return false
        if(locations.length && !locations.includes(company.region) && !locations.includes(company.county)) return false
        return true
      }).sort((a, b) => {
        if(this.sortMethod === 'type') return a.type.localeCompare(b.type)
        if(this.sortMethod === 'name') return a.name.localeCompare(b.name)
        return b.created_at - a.created_at
      })
    }
  },
  components: {
    MapView,
    CardView
  },
  methods: {
    openNewCompany(e, panel) {
      e.stopPropagation()
      window.dispatchEvent(new CustomEvent('openNewCompany', { detail: {panel} }))
    },
    toggleMenu(e) {
      e.stopPropagation()
      this.menuOpened = !this.menuOpened
      window.dispatchEvent(new CustomEvent('toggleMenu'))
    },
    submitCompany() {
      this.$refs.companyForm.validate(valid => {
        if(valid) {
          if(this.saving) return false
          this.saving = true
          fetch(`https://tour-de-france-671b8.firebaseapp.com/api/companies`, {
            body: JSON.stringify(this.companyForm),
            method: "POST",
            headers: {
              'Content-Type': 'application/json'
            }
          })
          .then(res => {
            if(res.ok) {
              Notification.success({
                title: 'Application sent!',
                message: "Your application has been sent to our administrative team. We'll contact you as soon as possible."
              })
              this.dialog = null
              this.companyForm = {
                type: '',
                name: '',
                address: '',
                city: '',
                county: '',
                email: '',
                description: '',
                covid19: false,
                facebook: '',
                twitter: '',
                linkedin: '',
                instagram: '',
                website: '',
                phone: '',
              }
            }
            else {
              Notification.error({
                title: 'Error',
                message: "A similar company is already registered with this email address."
              })
            }
            this.saving = false
          })
          .catch(() => {
            Notification.error({
              title: 'Error',
              message: "An unknown error occurred. Please try again."
            })
            this.saving = false
          })
        }
      })
    }
  },
  mounted: function() {
    window.addEventListener('open-dialog', e => {
      this.dialog = e.detail.dialog || null
    })
    this.db().ref('/locations').orderByChild('verified').equalTo(true).on('value', val => {
      const locations = []
      val.forEach(loc => {
        const location = loc.val()
        location.key = loc.key
        locations.push(location)
      })
      this.allCompanies = locations
    })
    this.dialog = 'info'
  },
}
</script>

<style>
* {
  box-sizing: border-box;
  outline: none;
}

img {
  max-width: 100%;
}

button {
  cursor: pointer;
}

button.ic:focus {
  background: rgba(0,0,0,.08);
}

input,
textarea,
select {
  background: none;
  border: none;
  border-radius: 0;
  border-bottom: 1px solid;
  vertical-align: middle;
  height: 24px;
  padding: 0 8px;
  font: inherit;
  color: inherit;
  -webkit-appearance: none;
}

input[type="number"] {
  -moz-appearance: textfield;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

textarea {
  min-height: 100px;
  height: auto;
  min-width: 100%;
  max-width: 100%;
}

.fieldgroup {
  margin: 0 -8px;
}

.fieldgroup:after {
  content: '';
  display: block;
  clear: both;
}

.field {
  float: left;
  width: 100%;
  padding: 0 8px;
  margin-bottom: 24px;
}

.field.m {
  width: 50%;
}

.field input,
.field textarea,
.field select {
  width: 100%;
  display: block;
}

.buttons {
  text-align: right;
}

.toast {
  position: fixed;
  bottom: 16px;
  right: 16px;
  padding: 8px 32px;
  background: rgba(0,0,0,.8);
  border-radius: 4px;
  color: #fff;
  visibility: hidden;
  transition: all .16s ease-out;
  transform: translateY(calc(100% + 16px));
  z-index: 1000;
  max-width: 300px;
  font-size: 12px;
}

.toast[opened] {
  transform: none;
  visibility: visible;
}

.el-form-item--small.el-form-item {
  margin-bottom: 12px;
}

.el-form--label-top .el-form-item__label {
  line-height: 1;
  padding-bottom: 4px;
}

.el-col-12,
.el-col-24 {
  padding: 0 8px;
}

.form-actions {
  text-align: right;
  padding: 0 8px;
  margin-top: 24px;
}

.btn {
  position: relative;
  display: inline-block;
  height: 32px;
  line-height: 32px;
  width: auto;
  padding: 0 16px;
  background: #2980B7;
  color: #fff;
  border-radius: 4px;
  border: none;
  text-transform: uppercase;
  font-family: inherit;
  font-size: 14px;
  transition: background .16s;
}

.btn[loading] {
  color: transparent;
  pointer-events: none;
}

.btn[loading]:after {
  content: '';
  border-radius: 50%;
  width: 18px;
  height: 18px;
  margin: auto;
  font-size: 10px;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  text-indent: -9999em;
  border-top: 3px solid rgba(255, 255, 255, 0.2);
  border-left: 3px solid #ffffff;
  border-right: 3px solid rgba(255, 255, 255, 0.2);
  border-bottom: 3px solid rgba(255, 255, 255, 0.2);
  box-sizing: border-box;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-animation: load 1.1s infinite linear;
  animation: load 1.1s infinite linear;
}

@-webkit-keyframes load {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes load {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@media screen and (min-width: 1025px) {
  .btn:focus,
  .btn:hover {
    background: #1e5e85;
  }
}

a {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

a:focus {
  opacity: .8;
}

.ic {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
}

.ic:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 80%;
  height: 80%;
  margin: auto;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  background-color: transparent;
}

.ic.ic-info:before {
  background-image: url('assets/images/tdf_ic_info.svg');
}

.ic.ic-pin:before {
  background-image: url('assets/images/tdf_ic_pin.svg');
}

.ic.ic-bar:before {
  background-image: url('assets/images/tdf_ic_bar.svg');
}

.ic.ic-restaurant:before {
  background-image: url('assets/images/tdf_ic_restaurant.svg');
}

.ic.ic-association:before {
  background-image: url('assets/images/tdf_ic_association.svg');
}

.ic.ic-consultant:before {
  background-image: url('assets/images/tdf_ic_consultant.svg');
}

.ic.ic-hotel:before {
  background-image: url('assets/images/tdf_ic_hotel.svg');
}

.ic.ic-industry:before,
.ic.ic-government:before {
  background-image: url('assets/images/tdf_ic_business.svg');
}

.ic.ic-transportation:before {
  background-image: url('assets/images/tdf_ic_shipping.svg');
}

.ic.ic-fishing:before {
  background-image: url('assets/images/tdf_ic_fishing.svg');
}

.ic.ic-leisure:before {
  background-image: url('assets/images/tdf_ic_leisure.svg');
}

.ic.ic-photographer:before {
  background-image: url('assets/images/tdf_ic_photographer.svg');
}

.ic.ic-media:before {
  background-image: url('assets/images/tdf_ic_media.svg');
}

.ic.ic-artist:before {
  background-image: url('assets/images/tdf_ic_artist.svg');
}

.ic.ic-shop:before {
  background-image: url('assets/images/tdf_ic_cart.svg');
}

.ic.ic-education:before {
  background-image: url('assets/images/tdf_ic_school.svg');
}

.ic.ic-cafe:before {
  background-image: url('assets/images/tdf_ic_cafe.svg');
}

.ic.ic-face:before {
  background-image: url('assets/images/tdf_ic_face.svg');
}

.ic.ic-farming:before {
  background-image: url('assets/images/tdf_ic_nature.svg');
}

.ic.ic-technology:before {
  background-image: url('assets/images/tdf_ic_tech.svg');
}

.ic.ic-bakery:before {
  background-image: url('assets/images/tdf_ic_store.svg');
}

.ic.ic-fishmonger:before {
  background-image: url('assets/images/tdf_ic_store.svg');
}

.ic.ic-craft:before {
  background-image: url('assets/images/tdf_ic_layers.svg');
}

.ic.ic-health:before {
  background-image: url('assets/images/tdf_ic_health.svg');
}

.ic.ic-beauty:before {
  background-image: url('assets/images/tdf_ic_beauty.svg');
}

.ic.ic-other:before {
  background-image: url('assets/images/tdf_ic_location.svg');
}

.ic.ic-retailer:before,
.ic.ic-supplier:before {
  background-image: url('assets/images/tdf_ic_supplier.svg');
}

.ic.ic-wholesale:before {
  background-image: url('assets/images/tdf_ic_cart.svg');
}

.ic.ic-accountant:before {
  background-image: url('assets/images/tdf_ic_money.svg');
}

.ic.ic-hairdresser:before {
  background-image: url('assets/images/tdf_ic_cut.svg');
}

.ic.ic-car-dealer:before {
  background-image: url('assets/images/tdf_ic_car.svg');
}

.ic.ic-filter:before {
  background-image: url('assets/images/tdf_ic_filter.svg');
}

.ic.ic-search:before {
  background-image: url('assets/images/tdf_ic_search.svg');
}

.ic.ic-print:before {
  background-image: url('assets/images/tdf_ic_print.svg');
}

.ic.ic-website:before {
  background-image: url('assets/images/tdf_ic_website.svg');
}

.ic.ic-phone:before {
  background-image: url('assets/images/tdf_ic_phone.svg');
}

.ic.ic-facebook:before {
  background-image: url('assets/images/tdf_fb.svg');
}

.ic.ic-twitter:before {
  background-image: url('assets/images/tdf_tw.svg');
}

.ic.ic-gplus:before {
  background-image: url('assets/images/tdf_gplus.svg');
}

.ic.ic-linkedin:before {
  background-image: url('assets/images/tdf_linkd.svg');
}

.ic.ic-instagram:before {
  background-image: url('assets/images/tdf_instagram_color.svg');
}

.ic.ic-menu:before {
  background-image: url('assets/images/tdf_ic_menu.svg');
}

.ic.ic-close:before {
  background-image: url('assets/images/tdf_ic_close.svg');
}

#app {
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
}

header {
  position: relative;
  z-index: 100;
  background: #fff;
  height: 96px;
  flex-shrink: 0;
  overflow: hidden;
}

header .logo {
  height: 100%;
  text-align: center;
}

header .logo img {
  height: 100%;
  margin: auto;
}

header .ic-info {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 32px;
  width: 48px;
  height: 48px;
  margin: auto;
}

header .menu-btn {
  display: none;
  position: absolute;
  height: 48px;
  top: 0;
  bottom: 0;
  left: 24px;
  margin: auto;
}

header .menu-btn .ic {
  width: 48px;
  height: 48px;
}

.view {
  position: relative;
  width: 100%;
  flex-grow: 1;
  color: #2C3E50;
  flex-shrink: 1;
  min-height: 0;
}

.controls-bar {
  position: relative;
  display: flex;
  height: 58px;
  align-items: center;
  flex-shrink: 0;
  padding: 0 24px;
  background: #fff;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  z-index: 2;
}

.controls-bar .view-settings {
  display: flex;
  align-items: center;
  margin-right: 24px;
}

.controls-bar .el-select {
  margin-left: 24px;
}

.controls-bar .el-input {
  margin-left: auto;
  margin-right: 0;
  max-width: 320px;
}

.controls-bar .view-settings .el-switch {
  margin: 0 8px;
}

.controls-bar .view-settings i {
  font-size: 18px;
}

.controls-bar .view-settings i[active] {
  color: #409EFF;
}

footer {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  z-index: 100;
  flex-shrink: 0;
  padding: 0 24px;
}

footer .footer-left img {
  height: 44px;
  vertical-align: middle;
  margin-right: 4px;
}

footer .footer-left .embassy-info {
  font-size: 14px;
}

footer .footer-right {
  font-size: 12px;
}

footer .footer-right > * {
  display: inline-block;
  vertical-align: middle;
}

footer .footer-right img {
  height: 44px;
  margin-left: 4px;
}

.overlay {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, .72);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  opacity: 0;
  padding: 64px 48px;
  overflow: auto;
  pointer-events: none;
  transition: opacity .2s ease-in;
}

.overlay[fixed] {
  position: fixed;
  background: rgba(0, 0, 0, .88);
}

.overlay[opened] {
  opacity: 1;
  pointer-events: all;
}

.dialog {
  position: relative;
  width: 560px;
  max-width: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  transform: translateY(16px);
  transition: transform .2s ease-in;
}

.dialog.auto {
  width: auto;
}

.dialog > .ic-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  background-color: rgba(255, 255, 255, .5);
  border-radius: 50%;
}

.dialog .dialog-title {
  padding: 8px 32px;
  font-size: 21px;
  font-weight: bold;
}

.dialog .dialog-content {
  padding: 8px 24px 16px;
}

.overlay[opened] .dialog {
  transform: none;
}

.dialog .dialog-footer {
  font-size: 12px;
  padding: 12px 32px;
  border-top: 1px solid #eee;
}

.dialog .dialog-footer img {
  width: auto;
  height: 58px;
  max-width: calc(32% - 16px);
  object-fit: contain;
  margin: 16px 16px 12px 0;
}

.dialog .el-form .el-select {
  width: 100%;
}

.infos {
  padding: 0 8px;
  font-size: 14px;
}

.infos a,
footer .footer-right a,
.dialog-footer a {
  color: #409EFF;
  font-weight: bold;
}

.infos h4 {
  font-size: 18px;
  font-weight: bold;
  margin: 24px 0 16px;
}

.controls-filters .el-button {
  display: none;
}

@media screen and (max-width: 1280px) {
  .controls-filters {
    position: relative;
    margin-right: 16px;
  }
  .controls-filters .el-button {
    display: inline-flex;
  }
  .controls-filters .filters {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 14px;
    background: #fff;
    border-radius: 4px;
    padding: 8px;
    border: 1px solid #eee;
    box-shadow: 0 8px 16px rgba(136, 152, 170, .15);
    opacity: 0;
    pointer-events: none;
    transition: opacity .2s ease-in;
  }
  .controls-filters .filters[opened] {
    opacity: 1;
    pointer-events: all;
  }
  .controls-filters .filters .el-select {
    margin: 8px 0 0 0;
  }
}

@media screen and (max-width: 1170px) {
  header {
    height: 65px;
  }
  header .menu-btn {
    display: block;
  }
  header .ic-info {
    right: 24px;
  }
}

@media screen and (max-width: 750px) {
  footer {
    display: none;
  }
}

@media screen and (max-width: 650px) {
  .overlay {
    position: fixed;
    padding: 12px;
  }
  .dialog {
    width: 100%;
  }
  .controls-bar {
    padding: 0 16px;
  }
}

@media screen and (max-height: 830px) {
  .overlay {
    align-items: flex-start;
  }
}

@media print {
  header,
  footer {
    display: none;
  }
}
</style>
